import { Link } from "react-router-dom";
import { PenSquare, MessagesSquare } from "lucide-react";
import { Button } from "../../components/ui/button";
import { useApi } from "../../lib/useApi";
import { useAuth } from "../../context/AuthContext";
import { Loader, ErrorState, EmptyState, PageHeader } from "../../components/common/States";
import AnnouncementsFeed from "../../components/comms/AnnouncementsFeed";
import { StatusBadge, Stars } from "../../components/common/Badges";
import { fmtDate } from "../../lib/labels";

const Kpi = ({ label, value, testId, tone = "" }) => (
  <div className={`card-surface p-5 ${tone}`} data-testid={testId}><p className="eyebrow">{label}</p><p className="mt-3 font-display text-3xl font-bold">{value}</p></div>
);

export const FeedbackRow = ({ f }) => (
  <Link to={`/mon-espace/feedbacks/${f.id}`} data-testid={`user-feedback-row-${f.id}`} className="block px-5 py-4 transition-colors hover:bg-slate-50">
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-medium">{f.company_name}</span><StatusBadge value={f.status} /><Stars value={f.rating} />
      <span className="ml-auto font-mono text-xs text-slate-400">{f.tracking_code}</span>
    </div>
    <p className="mt-1 line-clamp-1 text-sm text-slate-600">{f.title ? <b>{f.title} — </b> : null}{f.message}</p>
    <p className="mt-1 text-xs text-slate-400">{fmtDate(f.created_at)}</p>
  </Link>
);

export default function UserDashboard() {
  const { user } = useAuth();
  const { data, loading, error, reload } = useApi("/me/dashboard");
  if (loading) return <Loader />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const s = data.by_status;
  return (
    <div>
      <PageHeader eyebrow="Mon espace" title={`Bonjour ${user.name}`} description="Suivez vos avis et réclamations et échangez avec les organisations."
        actions={<Button asChild className="rounded-full" data-testid="user-dashboard-give-button"><Link to="/mon-espace/deposer"><PenSquare className="mr-1.5 h-4 w-4" />Déposer un feedback</Link></Button>} />
      {(s.attente_usager || 0) > 0 && (
        <Link to="/mon-espace/feedbacks?status=attente_usager" className="mb-6 block rounded-xl border border-violet-200 bg-violet-50 px-5 py-4 text-sm text-violet-900" data-testid="user-pending-precision">
          {s.attente_usager} organisation(s) attendent une précision de votre part — répondre maintenant
        </Link>
      )}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi testId="user-kpi-total" label="Feedbacks déposés" value={data.total} />
        <Kpi testId="user-kpi-open" label="En cours" value={(s.nouveau || 0) + (s.en_cours || 0) + (s.attente_usager || 0)} />
        <Kpi testId="user-kpi-resolved" label="Résolus / clos" value={(s.resolu || 0) + (s.clos || 0)} />
        <Kpi testId="user-kpi-unread" label="Notifications non lues" value={data.unread} />
      </div>
      <div className="mt-8"><AnnouncementsFeed url="/me/announcements" title="Annonces des organisations que vous avez évaluées" testId="user-announcements" /></div>
      <div className="mt-8">
        <div className="mb-3 flex items-center justify-between"><p className="font-display font-semibold">Activité récente</p><Link to="/mon-espace/feedbacks" className="text-sm font-medium text-blue-600" data-testid="user-see-all">Tout voir</Link></div>
        {!data.recent.length ? (
          <EmptyState icon={MessagesSquare} title="Aucun feedback pour l'instant" text="Recherchez une organisation pour déposer votre premier avis." />
        ) : <div className="card-surface divide-y">{data.recent.map((f) => <FeedbackRow key={f.id} f={f} />)}</div>}
      </div>
    </div>
  );
}
